import React from 'react';
import PropTypes from 'prop-types';
import { Input } from 'reactstrap';
import { Link } from 'react-router-dom';

import placeApi from 'api/placeApi';
import { ReactComponent as AddIcon } from 'assets/images/plus.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg';
import { LOCATIONS } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import randomIDString from 'utils/randomId';
import { apiErrorHandler, showSuccess, showToastError } from 'utils';

import styles from './NearLocation.module.scss';

const parseDistance = (value) => {
  const parsed = parseFloat(String(value).replace(',', '.'));

  return Number.isNaN(parsed) ? null : parsed;
};

const mapLandmarksToLocations = (nearbyLandmarks = []) => nearbyLandmarks.map((item) => ({
  id: randomIDString(),
  name: item.name || '',
  distance: item.distance != null ? String(item.distance) : '',
}));

function NearLocationList(props) {
  const { placeId, placeDetail, onSaveSuccess } = props;

  const [locations, setLocations] = React.useState([]);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (placeDetail?._id) {
      setLocations(mapLandmarksToLocations(placeDetail.nearbyLandmarks));
    }
  }, [placeDetail]);

  const onChangeLocation = React.useCallback((id, field, value) => {
    setLocations((prev) => prev.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  }, []);

  const onDeleteLocation = React.useCallback((id) => {
    setLocations((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const onAddLocation = React.useCallback(() => {
    setLocations((prev) => [
      ...prev,
      {
        id: randomIDString(),
        name: '',
        distance: '',
      },
    ]);
  }, []);

  const onSave = React.useCallback(async () => {
    if (!placeId || isSaving) return;

    const hasIncompleteRow = locations.some((item) => {
      const name = item.name?.trim();
      const distance = parseDistance(item.distance);

      return (name && distance === null) || (!name && item.distance);
    });

    if (hasIncompleteRow) {
      showToastError('Vui lòng nhập đầy đủ tên địa điểm và khoảng cách');
      return;
    }

    const nearbyLandmarks = locations
      .map((item) => ({
        name: item.name?.trim(),
        distance: parseDistance(item.distance),
      }))
      .filter((item) => item.name && item.distance !== null);

    try {
      setIsSaving(true);
      await placeApi.updateNearbyLandmarks({ placeId, nearbyLandmarks });
      showSuccess('Cập nhật địa điểm lân cận thành công');
      onSaveSuccess?.();
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, locations, onSaveSuccess, placeId]);

  const renderFields = (nameValue, distanceValue, onNameChange, onDistanceChange) => (
    <div className={styles.FieldsWrapper}>
      <div className={styles.Field}>
        <Input
          className={styles.Input}
          placeholder="Nhập tên địa điểm"
          value={nameValue}
          onChange={onNameChange}
        />
      </div>

      <div className={`${styles.Field} ${styles.DistanceField}`}>
        <Input
          className={styles.Input}
          placeholder="Khoảng cách"
          value={distanceValue}
          onChange={onDistanceChange}
        />
        <span className={styles.Unit}>km</span>
      </div>
    </div>
  );

  return (
    <div className={styles.ListWrapper}>
      <div className={styles.NearLocationList}>
        <div className={styles.ListHeader}>
          <div className={styles.FieldsWrapper}>
            <span className={styles.ListHeaderLabel}>Địa điểm</span>
            <span className={styles.ListHeaderLabel}>Khoảng cách</span>
          </div>
          <span className={styles.ListHeaderAction} aria-hidden />
        </div>

        {locations.map((item) => (
          <div key={item.id} className={styles.ListRow}>
            {renderFields(
              item.name,
              item.distance,
              (e) => onChangeLocation(item.id, 'name', e.target.value),
              (e) => onChangeLocation(item.id, 'distance', e.target.value),
            )}

            <button
              type="button"
              className={styles.DeleteButton}
              onClick={() => onDeleteLocation(item.id)}
              aria-label="Xóa địa điểm"
              disabled={isSaving}
            >
              <DeleteIcon />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className={styles.AddButton}
        onClick={onAddLocation}
        disabled={isSaving}
      >
        <AddIcon />
        <span className={styles.AddButtonText}>Thêm địa điểm</span>
      </button>

      <div className={styles.ButtonsWrapper}>
        <Link
          className={styles.CancelButtonLink}
          to={`${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`}
        >
          <button
            type="button"
            className={styles.CancelButton}
            disabled={isSaving}
          >
            Hủy
          </button>
        </Link>

        <button
          type="button"
          className={styles.SaveButton}
          onClick={onSave}
          disabled={isSaving || !placeId}
        >
          Lưu
        </button>
      </div>
    </div>
  );
}

NearLocationList.propTypes = {
  placeId: PropTypes.string,
  placeDetail: PropTypes.instanceOf(Object),
  onSaveSuccess: PropTypes.func,
};

NearLocationList.defaultProps = {
  placeId: '',
  placeDetail: INITIAL_VALUE.OBJECT,
  onSaveSuccess: INITIAL_VALUE.FUNCTION,
};

export default NearLocationList;
